import { useSelector } from "react-redux";
import { useAcademicYear } from "../../../../core/common/context/AcademicYearContext";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import "./index.css";
import { getAllStudentRequests } from "../../../../api/request";

const StudentRequestList = () => {
  const userData = useSelector((state: any) => state.auth.user);
  const { selectedYear } = useAcademicYear();
  const [allRequests, setAllRequests] = useState<any>([]);

  //Initialisation des state utilisés
  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données

  const fetchAllRequests = async () => {
    try {
      const response = await getAllStudentRequests(userData?.id);
      const requests = Array.isArray(response)
        ? response
        : (response?.data ?? []);
      // console.log("Toutes les demandes", requests);
      setAllRequests(requests);

      setHasLoaded(true);
      setIsLoading(false);
    } catch (error: any) {
      // console.log("erreur de chargement des demandes : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );

      // Option: reset les demandes en cas d'erreur
      setAllRequests([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    fetchAllRequests();
  }, []);

  const statusConfig: Record<string, { className: string; icon: string }> = {
    Déposé: {
      className: "bg-secondary",
      icon: "ti ti-inbox",
    },
    "En Cours de Traitement": {
      className: "bg-warning",
      icon: "ti ti-loader",
    },
    Traité: {
      className: "bg-success",
      icon: "ti ti-check",
    },
  };

  const taskStatusConfig: Record<string, { className: string; icon: string }> =
    {
      Ouvert: {
        className: "bg-warning",
        icon: "ti ti-loader",
      },
      "En Cours": {
        className: "bg-warning",
        icon: "ti ti-loader",
      },
      Traité: {
        className: "bg-success",
        icon: "ti ti-check",
      },
      Rejeté: {
        className: "bg-danger",
        icon: "ti ti-x",
      },
    };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Mes demandes</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    {selectedYear?.Student?.Level?.abbreviation}{" "}
                    {selectedYear?.Student?.Class?.label}
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Demandes
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}

          <div className="row g-3">
            {/* ITEM 1 */}
            {allRequests.length > 0 ? (
              allRequests.map((request: any) => {
                const status = request.statut;
                const statusStyle =
                  statusConfig[status] ?? statusConfig["Déposé"];

                return (
                  <div
                    className="col-12 col-md-6"
                    key={request.id ?? request.number_request}
                  >
                    <div
                      className="accordion accordionicon-none"
                      id="accordionExample1"
                    >
                      <div
                        className="accordion-item"
                        style={{ border: "1px solid #B5B9C3" }}
                      >
                        <h2
                          className="accordion-header"
                          id="heading1"
                          style={{ borderBottom: "1px solid #B5B9C3" }}
                        >
                          <button
                            className="accordion-button d-flex justify-content-between align-items-start"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapse1"
                            aria-expanded="true"
                            aria-controls="collapse1"
                          >
                            {/* Partie texte limité */}
                            <div
                              className="d-flex flex-column text-start me-3"
                              style={{ maxWidth: "calc(100% - 80px)" }}
                            >
                              <span className="fw-bold text-dark">
                                Demande {request.priority} #
                                {request.number_request}
                              </span>
                              <small
                                className="text-muted"
                                style={{
                                  wordBreak: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                <i className="ti ti-notes me-1" />
                                {request.Class.Level.abbreviation} -{" "}
                                {request.Class.label}
                              </small>
                            </div>

                            {/* Badge à droite */}
                            <span
                              className={`badge ${statusStyle.className} flex-shrink-0`}
                            >
                              <i
                                className={`ti ${statusStyle.icon} text-white fw-bold me-1`}
                              />
                              {status}
                            </span>
                          </button>
                        </h2>

                        <div
                          id="collapse1"
                          className="accordion-collapse collapse"
                          data-bs-parent="#accordionExample1"
                        >
                          <div className="accordion-body">
                            {/* Contenu de l’item */}
                            {(request.Taches?.length ?? 0) > 0 ? (
                              request.Taches.map((task: any) => {
                                const taskStatus = task.statut_tache;
                                const taskStatusStyle =
                                  taskStatusConfig[taskStatus] ??
                                  taskStatusConfig.Ouvert;
                                return (
                                  <div
                                    className="notice-item mb-3 p-3 rounded"
                                    style={{ border: "1px solid #B5B9C3" }}
                                    key={task.id ?? task._id}
                                  >
                                    <div className="d-flex flex-column gap-2">
                                      {/* Icône + titre + description + badge */}
                                      <div className="d-flex justify-content-between align-items-start gap-2">
                                        <div className="d-flex align-items-start flex-grow-1 min-w-0">
                                          <span className="bg-primary-transparent avatar avatar-md me-2 rounded-circle flex-shrink-0">
                                            <i className="ti ti-books fs-16" />
                                          </span>

                                          <div className="min-w-0">
                                            <h6 className="mb-1 text-wrap-break">
                                              {task.TacheType.label}
                                            </h6>

                                            <p className="mb-0 text-muted text-wrap-break">
                                              <i className="ti ti-user me-1" />
                                              {task.UserProfil.firstname}{" "}
                                              {task.UserProfil.lastname}
                                            </p>

                                            <p className="mb-0 text-muted text-wrap-break">
                                              <i className="ti ti-notes me-1" />
                                              {task.consigne}
                                            </p>
                                          </div>
                                        </div>

                                        <span
                                          className={`badge ${taskStatusStyle.className} rounded-circle p-2 flex-shrink-0`}
                                        >
                                          <i
                                            className={`${taskStatusStyle.icon} text-white fw-bold`}
                                          />
                                        </span>
                                      </div>

                                      {/* Réponse */}
                                      {task.AnswerTaches?.length > 0 && (
                                        <div className="mt-2">
                                          <div
                                            className="bg-light border p-2 text-muted"
                                            style={{ wordWrap: "break-word" }}
                                          >
                                            <strong>Réponse :</strong>{" "}
                                            {[...task.AnswerTaches].sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()).map((answer: any) => <div key={answer.id} className="border-top mt-2 pt-2"><small>{answer.createdAt ? new Date(answer.createdAt).toLocaleString('fr-FR') : ''}{answer.statut_tache ? ` · ${answer.statut_tache}` : ''}</small><p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{answer.observation}</p></div>)}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <p>rien</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <i className="ti ti-file-off fs-1 text-muted mb-3 d-block" />
                  <h6 className="mb-1">Aucune demande trouvée</h6>
                  <p className="text-muted mb-3">
                    Vous n’avez encore soumis aucune demande.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentRequestList;
